import React, { useContext, useEffect, useState } from "react";
import { SessionContext } from "contexts";
import { ProcedureType, TScreen, ViewType } from "api/type";
import {
  postPatientsImagesBatch,
  postTransforms,
  postTransformsBatch,
  TPostPatientsImagesBatchRequest,
  TPostPatientsImagesBatchResponse,
  TPostTransformsBatchRequest,
  TPostTransformsBatchResponse,
  TPostTransformsRequest,
  TPostTransformsResponse,
} from "api/clinicos";
import ScreenSelectModel from "./ScreenSelectModel";
import ScreenVisualize from "./ScreenVisualize";

import NormalText from "components/NormalText";
import { mixpanelTrack } from "utils/mixpanel";
import ScreenContext from "contexts/screen";

type RootOnboardingAdvertiseProps = {};

const RootOnboardingAdvertise: React.FC<RootOnboardingAdvertiseProps> = () => {
  // Context and State Declarations
  const session = useContext(SessionContext);
  const [screen, setScreen] = useContext(ScreenContext);

  const [procedure, setProcedure] = useState<ProcedureType | null>(null);
  const [selectedImageUrlOrBlob, setSelectedImageUrlOrBlob] = useState<
    string | Blob
  >("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);

  const [postTransformsResponse, setPostTransformsResponse] =
    useState<TPostTransformsResponse | null>(null);
  const [postTransformsBatchResponse, setPostTransformsBatchResponse] =
    useState<TPostTransformsBatchResponse | null>(null);
  const [currentBatchId, setCurrentBatchId] = useState<string>("");

  const handleSetIsFormSubmittedTrue = () => {
    setIsFormSubmitted(true);
  };

  useEffect(() => {
    setScreen(TScreen.SELECT_MODEL_SCREEN);
  }, [setScreen]);

  // Effect for setting procedure from URL
  useEffect(() => {
    const currentUrl = window.location.search;
    const urlSearchParams = new URLSearchParams(currentUrl);
    const procedureUrlParam = urlSearchParams.get("procedure");
    setProcedure(procedureUrlParam as ProcedureType);

    if (!procedureUrlParam) {
      setErrorMessage(
        "Please pass in a procedure in the URL, e.g. ?procedure=BBL, ?procedure=BREAST_AUGMENTATION, ?procedure=BREAST_REDUCTION, ?procedure=BREAST_LIFT, ?procedure=TUMMY_TUCK",
      );
    }

    mixpanelTrack("Ad View", [
      {
        propertyName: "Procedure",
        propertyValue: procedureUrlParam ?? "Failed to get procedure",
      },
      {
        propertyName: "Version",
        propertyValue: "V5",
      },
    ]);
  }, []);

  // Effect: Fetch After Image URLs
  useEffect(() => {
    setPostTransformsResponse(null);
    setPostTransformsBatchResponse(null);
    const fetchAfterImageUrls = async () => {
      try {
        if (!session || !selectedImageUrlOrBlob || !procedure) return;

        let beforeImageUrl = "";
        let viewType: ViewType | null;

        // Determine the view type based on the selectedProcedure
        switch (procedure) {
          case ProcedureType.BREAST_AUGMENTATION:
          case ProcedureType.BREAST_REDUCTION:
          case ProcedureType.BREAST_LIFT:
          case ProcedureType.BREAST_LIFT_AND_AUGMENTATION:
            viewType = ViewType.TORSO_FRONT;
            break;
          case ProcedureType.TUMMY_TUCK:
            viewType = ViewType.BODY_FRONT;
            break;
          case ProcedureType.BBL:
            viewType = ViewType.BODY_BACK;
            break;
          default:
            throw new Error("Invalid procedure type");
        }

        // Handle image upload and retrieval
        if (selectedImageUrlOrBlob instanceof Blob) {
          const body: TPostPatientsImagesBatchRequest = {
            images: [
              {
                view_type: viewType,
                procedure_types: [procedure],
              },
            ],
          };
          const result: TPostPatientsImagesBatchResponse =
            await postPatientsImagesBatch(session, body, [
              selectedImageUrlOrBlob,
            ]);
          if (result.presigned_urls.length !== 1) {
            throw new Error("Invalid number of presigned urls");
          }

          beforeImageUrl = result.presigned_urls[0].get_url;
          setCurrentBatchId(result.batch_id);

          const secondBody: TPostTransformsBatchRequest = {
            batch_id: result.batch_id,
            request: [
              {
                image_url: beforeImageUrl,
                procedure_types: [procedure],
                procedure_view: viewType,
              },
            ],
          };
          const secondResult: TPostTransformsBatchResponse =
            await postTransformsBatch(session, secondBody);
          setPostTransformsBatchResponse(secondResult);
        } else if (typeof selectedImageUrlOrBlob === "string") {
          beforeImageUrl = selectedImageUrlOrBlob;
          const body: TPostTransformsRequest = {
            image_url: beforeImageUrl,
            procedure_types: [procedure],
            procedure_view: viewType,
          };
          const result: TPostTransformsResponse = await postTransforms(
            session,
            body,
          );
          setPostTransformsResponse(result);
        }
      } catch (error) {
        if (error instanceof Error) {
          if (error.message.includes("401")) {
            setErrorMessage(
              "Session has expired. Page will refresh in 3 seconds.",
            );
            setTimeout(() => {
              window.location.reload();
            }, 3000);
          } else {
            setErrorMessage(error.message);
          }
        } else {
          setErrorMessage("Error fetching after image urls");
        }
      }
    };
    fetchAfterImageUrls();

    // NOTE: It is safe to not include selectedImageUrlOrBlob in the dependency array
    // Including it will cause a bug where the user goes back to the SelectModel screen,
    // selects a procedure, and then the Visualize screen shows 2 simulations.
    // eslint-disable-next-line
  }, [selectedImageUrlOrBlob, session]);

  // Render Error Messages
  if (!session) return <NormalText textContent="Must have a session" />;
  if (errorMessage) return <NormalText textContent={errorMessage} />;
  if (procedure === null) return null;

  // Render Screens
  const renderScreen = () => {
    switch (screen) {
      case TScreen.SELECT_MODEL_SCREEN:
        return (
          <ScreenSelectModel
            saveSelectedImageUrlOrBlob={setSelectedImageUrlOrBlob}
            procedure={procedure}
          />
        );
      case TScreen.SIMULATION_VIEW_SCREEN:
        return (
          <ScreenVisualize
            batchId={currentBatchId}
            selectedProcedure={procedure}
            isFormSubmitted={isFormSubmitted}
            handleSetIsFormSubmittedTrue={handleSetIsFormSubmittedTrue}
            postTransformsResponse={postTransformsResponse}
            postTransformsBatchResponse={postTransformsBatchResponse}
          />
        );
      case TScreen.NONE:
        return null;
      default:
        return <NormalText textContent="Invalid screen" />;
    }
  };

  return <div id="onboarding-advertise-container">{renderScreen()}</div>;
};

export default RootOnboardingAdvertise;
